import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { localDayKey } from '@/lib/publishing/bucketPublishesByLocalDay';
import {
  datetimeLocalValueToIso,
  isoToDatetimeLocalValue,
  viewerTimeZone,
} from '@/lib/publishing/calendarMonth';
import { extractPublishErrorCode, mapPublishError } from '@/lib/publishing/mapPublishError';
import { isScheduledAtTooSoon } from '@/lib/publishing/isScheduledAtTooSoon';
import { minScheduleDatetimeLocal } from '@/lib/publishing/minScheduleDatetimeLocal';
import { PublishQueueBadge } from '@/components/publishing/PublishQueueBadge';
import { ScheduleDatetimePicker } from '@/components/publishing/ScheduleDatetimePicker';
import {
  isFacebookPublishableConnection,
  isInstagramPublishableConnection,
  isSendableConnection,
  isYouTubePublishableConnection,
} from '@/lib/publishing/resolvePublishEligibility';
import { resolveScheduledSheetMode } from '@/lib/publishing/resolveScheduledSheetMode';
import {
  isPublishPlatform,
  type PublishConnection,
  type PublishPlatform,
  type PublishRecord,
} from '@/lib/publishing/types';
import { useSocialPostingPlatforms } from '@/hooks/use-social-posting-platforms';
import { usePostableClipExpiry } from '@/hooks/use-postable-clip-expiry';
import { resolvePublishedPostUrl } from '@/lib/publishing/buildSocialPostUrl';
import { isPostableClip } from '@/lib/publishing/isPostableClip';
import { MY_SOCIAL_CONNECTIONS } from '@/queries/queries';
import {
  CANCEL_MY_SCHEDULED_PUBLISH,
  CREATE_MY_SCHEDULED_PUBLISH,
  MY_CLIP_DISPLAY,
  MY_POSTABLE_CLIPS,
  UPDATE_MY_SCHEDULED_PUBLISH,
} from '@/queries/scheduledPublishing';

const TITLE_LIMIT = 100;
const DESCRIPTION_LIMIT = 5000;
const CAPTION_LIMIT = 2200;

type PrivacyStatus = 'public' | 'unlisted' | 'private';

type PostableClip = {
  id: string;
  name?: string | null;
  thumbnail_url?: string | null;
  finished_video_url?: string | null;
  createdAt?: string | null;
  clipping_status?: string | null;
  preparing_status?: string | null;
  rendering_status?: string | null;
  processing_status?: string | null;
};

export type ScheduledSheetPublish = PublishRecord & {
  clip_name?: string | null;
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  publish?: ScheduledSheetPublish | null;
  defaultScheduledAt?: string | null;
  dayKey?: string | null;
  timeZone?: string;
  onSaved?: () => void;
  onCancelled?: () => void;
}

function sheetSubmissionTarget({
  publish,
  defaultScheduledAt,
  dayKey,
  timeZone,
}: {
  publish?: ScheduledSheetPublish | null;
  defaultScheduledAt?: string | null;
  dayKey?: string | null;
  timeZone: string;
}): { publishId: string; dayKey: string } {
  const publishId = publish?.id || 'create';
  if (dayKey) return { publishId, dayKey };
  const iso = publish?.scheduled_at || defaultScheduledAt || '';
  return { publishId, dayKey: iso ? localDayKey(iso, timeZone) : '' };
}

function isEligibleConnection(connection: PublishConnection, platform: PublishPlatform): boolean {
  if (platform === 'tiktok') return isSendableConnection(connection);
  if (platform === 'youtube') return isYouTubePublishableConnection(connection);
  if (platform === 'facebook') return isFacebookPublishableConnection(connection);
  return isInstagramPublishableConnection(connection);
}

function seedPlatform(publish?: ScheduledSheetPublish | null): PublishPlatform {
  return publish && isPublishPlatform(publish.platform) ? publish.platform : 'tiktok';
}

function seedPrivacy(value?: string | null): PrivacyStatus {
  if (value === 'unlisted' || value === 'private' || value === 'public') return value;
  return 'public';
}

export const ScheduledPublishSheet: React.FC<Props> = ({
  open,
  onOpenChange,
  publish = null,
  defaultScheduledAt = null,
  dayKey = null,
  timeZone: timeZoneProp,
  onSaved,
  onCancelled,
}) => {
  const { t, i18n } = useTranslation();
  const timeZone = timeZoneProp || viewerTimeZone();
  const canPostToFacebook = useSocialPostingPlatforms().includes('facebook');
  const platformOptions = (['tiktok', 'instagram', 'facebook', 'youtube'] as const)
    .filter((value) => value !== 'facebook'
      || canPostToFacebook
      || publish?.platform === 'facebook');
  const mode = resolveScheduledSheetMode(publish);
  const readOnly = mode === 'readonly';
  const submissionTarget = sheetSubmissionTarget({
    publish, defaultScheduledAt, dayKey, timeZone,
  });
  const targetVersionRef = useRef(0);
  const mountedRef = useRef(false);
  const currentTargetRef = useRef({ open, ...submissionTarget });
  currentTargetRef.current = { open, ...submissionTarget };

  const [clipId, setClipId] = useState('');
  const [clipSearch, setClipSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [platform, setPlatform] = useState<PublishPlatform>('tiktok');
  const [connectionId, setConnectionId] = useState('');
  const [datetimeLocal, setDatetimeLocal] = useState('');
  const [caption, setCaption] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [privacyStatus, setPrivacyStatus] = useState<PrivacyStatus>('public');
  const [madeForKids, setMadeForKids] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(clipSearch.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [clipSearch]);

  useEffect(() => {
    targetVersionRef.current += 1;
    if (!open) return;
    const scheduledIso = publish?.scheduled_at || defaultScheduledAt || '';
    setClipId(publish?.clip_id || '');
    setClipSearch('');
    setDebouncedSearch('');
    setPlatform(seedPlatform(publish));
    setConnectionId(publish?.social_connection_id || '');
    setDatetimeLocal(scheduledIso ? isoToDatetimeLocalValue(scheduledIso, timeZone) : '');
    setCaption((publish?.caption || '').slice(0, CAPTION_LIMIT));
    setTitle((publish?.title || '').slice(0, TITLE_LIMIT));
    setDescription((publish?.description || '').slice(0, DESCRIPTION_LIMIT));
    setPrivacyStatus(seedPrivacy(publish?.privacy_status));
    setMadeForKids(publish?.made_for_kids === true);
    setFormError(null);
  }, [open, publish, defaultScheduledAt, dayKey, timeZone]);

  const maxExpiryDays = usePostableClipExpiry(open && !readOnly);
  const { data: clipData } = useQuery(MY_POSTABLE_CLIPS, {
    variables: { search: debouncedSearch || undefined, limit: 30, offset: 0 },
    skip: !open || readOnly,
  });
  const { data: connData } = useQuery(MY_SOCIAL_CONNECTIONS, { skip: !open });
  const { data: clipDisplayData } = useQuery(MY_CLIP_DISPLAY, {
    variables: { id: publish?.clip_id },
    skip: !open || !publish?.clip_id,
  });
  const [createPublish, { loading: creating }] = useMutation(CREATE_MY_SCHEDULED_PUBLISH);
  const [updatePublish, { loading: updating }] = useMutation(UPDATE_MY_SCHEDULED_PUBLISH);
  const [cancelPublish, { loading: cancelling }] = useMutation(CANCEL_MY_SCHEDULED_PUBLISH);
  const saving = creating || updating;

  const clips: PostableClip[] = useMemo(
    () => ((clipData?.myPostableClips || []) as PostableClip[])
      .filter((clip) => isPostableClip(clip, { maxExpiryDays, now: new Date() })),
    [clipData, maxExpiryDays],
  );
  const allConnections: PublishConnection[] = useMemo(
    () => ((connData?.mySocialConnections || []) as PublishConnection[])
      .filter((connection) => isPublishPlatform(connection.platform)),
    [connData],
  );
  const connections = useMemo(
    () => allConnections.filter((connection) => connection.platform === platform),
    [allConnections, platform],
  );
  const eligible = useMemo(
    () => connections.filter((connection) => isEligibleConnection(connection, platform)),
    [connections, platform],
  );
  const selectedConnectionId = eligible.some(({ id }) => id === connectionId)
    ? connectionId
    : eligible[0]?.id || '';
  const accountConnection = allConnections.find((connection) => (
    connection.id === (publish?.social_connection_id || selectedConnectionId)
  ));
  const accountLabel = accountConnection?.display_name
    || accountConnection?.username
    || '—';

  const selectedClip = clips.find((clip) => clip.id === clipId);
  const selectedClipLabel = selectedClip?.name
    || clipDisplayData?.myClip?.name
    || publish?.clip_name
    || t('publishing.calendar.untitled');
  const postUrl = resolvePublishedPostUrl({
    platform,
    status: publish?.status,
    providerPostId: publish?.provider_post_id,
    username: accountConnection?.username,
  });
  const scheduledAtIso = datetimeLocalValueToIso(datetimeLocal, timeZone);
  const datetimeLabel = scheduledAtIso
    ? new Intl.DateTimeFormat(i18n.language, {
      timeZone,
      dateStyle: 'medium',
      timeStyle: 'short',
      hourCycle: 'h23',
    }).format(new Date(scheduledAtIso))
    : '—';
  const youtubeTitleValid = title.trim().length > 0 && title.length <= TITLE_LIMIT;
  const canSave = !!clipId
    && !!selectedConnectionId
    && !!scheduledAtIso
    && (platform !== 'youtube' || youtubeTitleValid)
    && description.length <= DESCRIPTION_LIMIT
    && caption.length <= CAPTION_LIMIT;

  const close = () => {
    targetVersionRef.current += 1;
    onOpenChange(false);
  };

  const isCurrentSubmission = (submittedVersion: number, submitted: { publishId: string; dayKey: string }) => (
    mountedRef.current
    && submittedVersion === targetVersionRef.current
    && currentTargetRef.current.open
    && currentTargetRef.current.publishId === submitted.publishId
    && currentTargetRef.current.dayKey === submitted.dayKey
  );

  const goToSettings = () => {
    close();
    window.dispatchEvent(new CustomEvent('autoclipper:open-account-settings', {
      detail: { section: 'social', platform },
    }));
  };

  const handleSave = async () => {
    if (!scheduledAtIso || !canSave || saving) return;
    if (isScheduledAtTooSoon(scheduledAtIso, new Date())) {
      setFormError(t('publishing.schedule.tooSoon'));
      return;
    }

    const submittedVersion = targetVersionRef.current;
    const submitted = { ...submissionTarget };
    const input = {
      clipId,
      socialConnectionId: selectedConnectionId,
      platform,
      scheduledAt: scheduledAtIso,
      caption: platform === 'youtube' ? null : (caption || null),
      title: platform === 'youtube' ? title.trim() : null,
      description: platform === 'youtube' ? (description || null) : null,
      privacyStatus: platform === 'youtube' ? privacyStatus : null,
      madeForKids: platform === 'youtube' ? madeForKids : null,
    };

    try {
      if (mode === 'edit' && publish) {
        await updatePublish({ variables: { id: publish.id, input } });
      } else {
        await createPublish({ variables: { input } });
      }
      onSaved?.();
      if (!isCurrentSubmission(submittedVersion, submitted)) return;
      toast.success(t('publishing.schedule.saved'));
      close();
    } catch (error) {
      if (!isCurrentSubmission(submittedVersion, submitted)) return;
      setFormError(t(mapPublishError(extractPublishErrorCode(error))));
    }
  };

  const handleCancelSend = async () => {
    if (!publish || cancelling) return;
    const submittedVersion = targetVersionRef.current;
    const submitted = { ...submissionTarget };
    try {
      await cancelPublish({ variables: { id: publish.id } });
      onCancelled?.();
      if (!isCurrentSubmission(submittedVersion, submitted)) return;
      toast.success(t('publishing.schedule.cancelled'));
      close();
    } catch (error) {
      if (!isCurrentSubmission(submittedVersion, submitted)) return;
      setFormError(t(mapPublishError(extractPublishErrorCode(error))));
    }
  };

  const titleKey = mode === 'create'
    ? 'publishing.schedule.createTitle'
    : mode === 'edit'
      ? 'publishing.schedule.editTitle'
      : 'publishing.schedule.viewTitle';

  const renderAccountField = () => {
    if (readOnly) {
      return (
        <ReadOnlyField
          label={t('publishing.schedule.accountLabel')}
          value={accountLabel}
          testId="schedule-account-name"
        />
      );
    }
    if (connections.length === 0) {
      return (
        <div className="space-y-2" data-testid="schedule-no-connection">
          <p className="text-sm text-muted-foreground">{t('publishing.schedule.noConnectionBody')}</p>
          <Button type="button" onClick={goToSettings}>
            {t('publishing.reconnectAction')}
          </Button>
        </div>
      );
    }
    if (eligible.length === 0) {
      return (
        <div className="space-y-2" data-testid="schedule-reconnect">
          <p className="text-sm text-muted-foreground">{t('publishing.schedule.reconnectBody')}</p>
          <Button type="button" onClick={goToSettings}>
            {t('publishing.reconnectAction')}
          </Button>
        </div>
      );
    }
    return (
      <div className="space-y-1.5">
        <Label htmlFor="schedule-account">{t('publishing.schedule.accountLabel')}</Label>
        <Select value={selectedConnectionId} onValueChange={setConnectionId}>
          <SelectTrigger id="schedule-account" data-testid="schedule-account-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {eligible.map((connection) => (
              <SelectItem key={connection.id} value={connection.id}>
                {connection.display_name || connection.username}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) close();
    }}>
      <DialogContent
        data-testid="scheduled-publish-dialog"
        className="flex max-h-[85dvh] max-w-lg flex-col overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{t(titleKey)}</DialogTitle>
          <DialogDescription>
            {t(readOnly ? 'publishing.schedule.viewDescription' : 'publishing.schedule.description')}
          </DialogDescription>
          {publish?.publish_queue?.name ? (
            <PublishQueueBadge
              name={publish.publish_queue.name}
              className="w-fit text-xs"
              testId="schedule-queue-badge"
            />
          ) : null}
        </DialogHeader>

        <div className="mt-6 space-y-4">
          {readOnly ? (
            <ReadOnlyField
              label={t('publishing.schedule.clipLabel')}
              value={selectedClipLabel}
              testId="schedule-clip-name"
            />
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="schedule-clip-search">{t('publishing.schedule.clipLabel')}</Label>
              <Input
                id="schedule-clip-search"
                data-testid="schedule-clip-search"
                value={clipSearch}
                onChange={(event) => setClipSearch(event.target.value)}
                placeholder={t('publishing.schedule.clipSearch')}
              />
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border p-1">
                {clips.length === 0 ? (
                  <p className="px-2 py-3 text-sm text-muted-foreground">
                    {t('publishing.schedule.clipEmpty')}
                  </p>
                ) : clips.map((clip) => (
                  <button
                    key={clip.id}
                    type="button"
                    data-testid={`schedule-clip-${clip.id}`}
                    className={`flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground ${
                      clip.id === clipId ? 'bg-accent text-accent-foreground' : ''
                    }`}
                    aria-pressed={clip.id === clipId}
                    onClick={() => setClipId(clip.id)}
                  >
                    {clip.name || clip.id}
                  </button>
                ))}
              </div>
              {clipId ? (
                <p className="text-xs text-muted-foreground">{selectedClipLabel}</p>
              ) : null}
            </div>
          )}

          {readOnly ? (
            <ReadOnlyField
              label={t('publishing.schedule.platformLabel')}
              value={t(`publishing.schedule.platforms.${platform}`)}
            />
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="schedule-platform">{t('publishing.schedule.platformLabel')}</Label>
              <Select
                value={platform}
                onValueChange={(value) => {
                  if (isPublishPlatform(value)) {
                    setPlatform(value);
                    setConnectionId('');
                  }
                }}
              >
                <SelectTrigger id="schedule-platform" data-testid="schedule-platform-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {platformOptions.map((value) => (
                    <SelectItem key={value} value={value} disabled={value === 'facebook' && !canPostToFacebook}>
                      {t(`publishing.schedule.platforms.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {renderAccountField()}

          {readOnly ? (
            <ReadOnlyField
              label={t('publishing.schedule.datetimeLabel')}
              value={datetimeLabel}
            />
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="schedule-datetime">{t('publishing.schedule.datetimeLabel')}</Label>
              <ScheduleDatetimePicker
                id="schedule-datetime"
                testId="schedule-datetime"
                min={minScheduleDatetimeLocal(new Date(), timeZone)}
                value={datetimeLocal}
                timeZone={timeZone}
                onChange={(next) => {
                  setDatetimeLocal(next);
                  setFormError(null);
                }}
              />
            </div>
          )}

          {platform === 'youtube' ? (
            <>
              {readOnly ? (
                <ReadOnlyField label={t('publishing.youtube.titleLabel')} value={title || '—'} />
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="schedule-title">{t('publishing.youtube.titleLabel')}</Label>
                  <Input
                    id="schedule-title"
                    data-testid="schedule-title"
                    maxLength={TITLE_LIMIT}
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                  />
                </div>
              )}
              {readOnly ? (
                <ReadOnlyField
                  label={t('publishing.youtube.descriptionLabel')}
                  value={description || '—'}
                />
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="schedule-description">{t('publishing.youtube.descriptionLabel')}</Label>
                  <Textarea
                    id="schedule-description"
                    data-testid="schedule-description"
                    value={description}
                    maxLength={DESCRIPTION_LIMIT}
                    onChange={(event) => setDescription(event.target.value)}
                    rows={3}
                  />
                </div>
              )}
              {readOnly ? (
                <>
                  <ReadOnlyField
                    label={t('publishing.youtube.visibilityLabel')}
                    value={t(`publishing.youtube.visibility.${privacyStatus}`)}
                  />
                  <ReadOnlyField
                    label={t('publishing.youtube.audienceLabel')}
                    value={t(madeForKids
                      ? 'publishing.youtube.audience.madeForKids'
                      : 'publishing.youtube.audience.notMadeForKids')}
                  />
                </>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label>{t('publishing.youtube.visibilityLabel')}</Label>
                    <RadioGroup
                      value={privacyStatus}
                      onValueChange={(value) => setPrivacyStatus(seedPrivacy(value))}
                    >
                      {(['public', 'unlisted', 'private'] as const).map((value) => (
                        <label key={value} className="flex items-center gap-2 text-sm">
                          <RadioGroupItem value={value} />
                          {t(`publishing.youtube.visibility.${value}`)}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('publishing.youtube.audienceLabel')}</Label>
                    <RadioGroup
                      value={madeForKids ? 'yes' : 'no'}
                      onValueChange={(value) => setMadeForKids(value === 'yes')}
                    >
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="no" />
                        {t('publishing.youtube.audience.notMadeForKids')}
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="yes" />
                        {t('publishing.youtube.audience.madeForKids')}
                      </label>
                    </RadioGroup>
                  </div>
                </>
              )}
            </>
          ) : (
            readOnly ? (
              <ReadOnlyField label={t('publishing.instagram.captionLabel')} value={caption || '—'} />
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="schedule-caption">{t('publishing.instagram.captionLabel')}</Label>
                <Textarea
                  id="schedule-caption"
                  data-testid="schedule-caption"
                  value={caption}
                  maxLength={CAPTION_LIMIT}
                  onChange={(event) => setCaption(event.target.value)}
                  rows={3}
                />
              </div>
            )
          )}

          {postUrl ? (
            <ReadOnlyField
              label={t('publishing.schedule.postLink')}
              value={t('publishing.schedule.openPost', {
                platform: t(`publishing.schedule.platforms.${platform}`),
              })}
              href={postUrl}
              testId="schedule-post-link"
            />
          ) : null}

          {readOnly && publish?.status === 'failed' ? (
            <ReadOnlyField
              label={t('publishing.schedule.failReason')}
              value={t(mapPublishError(publish.fail_reason))}
            />
          ) : null}

          {formError ? (
            <p className="text-sm text-destructive" data-testid="schedule-form-error">
              {formError}
            </p>
          ) : null}
        </div>

        {mode !== 'readonly' ? (
          <DialogFooter className="mt-6 gap-2">
            {mode === 'edit' ? (
              <Button
                type="button"
                variant="destructive"
                data-testid="schedule-cancel-send"
                onClick={() => { void handleCancelSend(); }}
                disabled={cancelling}
              >
                {cancelling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {cancelling ? t('publishing.schedule.cancelling') : t('publishing.schedule.cancelSend')}
              </Button>
            ) : null}
            <Button
              type="button"
              data-testid="schedule-save"
              onClick={() => { void handleSave(); }}
              disabled={saving || !canSave || eligible.length === 0}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {saving ? t('publishing.schedule.saving') : t('publishing.schedule.save')}
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const ReadOnlyField = ({
  label,
  value,
  href,
  testId,
}: {
  label: string;
  value: string;
  href?: string | null;
  testId?: string;
}) => (
  <div className="space-y-1">
    <p className="text-xs font-medium text-muted-foreground">{label}</p>
    {href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"
      >
        <span className="break-all">{value}</span>
        <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      </a>
    ) : (
      <p className="text-sm break-words" data-testid={testId}>{value}</p>
    )}
  </div>
);

export default ScheduledPublishSheet;
